import { useState, useEffect } from 'react';
import { ArrowLeft, Clock, TrendingUp, DollarSign, Target, Play, Pause, CheckCircle2, Loader2 } from 'lucide-react';
import { dbService } from '../../services/firebase-db.service';
import { walletService } from '../../services/wallet.service';
import { auth } from '../../config/firebase.config';

interface ShiftManagementScreenProps {
  onBack: () => void;
}

function msToHours(ms: number): string {
  const totalMins = Math.floor(ms / 60000);
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  if (h === 0) return `${m}m`;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export function ShiftManagementScreen({ onBack }: ShiftManagementScreenProps) {
  const [isOnline, setIsOnline] = useState(false);
  const [shiftStartTime, setShiftStartTime] = useState('');
  const [loading, setLoading] = useState(true);
  const [togglingStatus, setTogglingStatus] = useState(false);

  const [todayStats, setTodayStats] = useState({
    hoursWorked: '—',
    deliveries: 0,
    earnings: '₦0',
  });

  const [weeklyDeliveries, setWeeklyDeliveries] = useState(0);
  const weeklyGoalTarget = 50;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const user = auth.currentUser;
    if (!user) { setLoading(false); return; }

    try {
      // Load rider profile for online status
      const profile = await dbService.getRiderProfile(user.uid);
      if (profile) {
        setIsOnline(profile.isOnline ?? false);
        // If online and shift started, show start time
        if (profile.isOnline && profile.shiftStartedAt) {
          const startDate = profile.shiftStartedAt.toDate
            ? profile.shiftStartedAt.toDate()
            : new Date(profile.shiftStartedAt);
          setShiftStartTime(startDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }));
        }
      }

      // Load wallet for today's earnings
      const wallet = await walletService.getWalletBalance(user.uid);
      const todayEarnings = wallet.todayEarnings ?? 0;

      // Load history for today's deliveries count
      const history = await dbService.getRiderJobHistory(user.uid, 100);
      const now = new Date();
      const todayDeliveries = history.filter((j: any) => {
        const ts = j.deliveredAt || j.completedAt || j.createdAt;
        if (!ts) return false;
        const date = ts.toDate ? ts.toDate() : new Date(ts);
        return now.getTime() - date.getTime() < 86400000;
      });
      const weeklyJobs = history.filter((j: any) => {
        const ts = j.deliveredAt || j.completedAt || j.createdAt;
        if (!ts) return false;
        const date = ts.toDate ? ts.toDate() : new Date(ts);
        return now.getTime() - date.getTime() < 7 * 86400000;
      });

      setWeeklyDeliveries(weeklyJobs.length);

      // Estimate hours worked today from shift start
      let hoursStr = '—';
      if (profile?.shiftStartedAt) {
        const start = profile.shiftStartedAt.toDate
          ? profile.shiftStartedAt.toDate()
          : new Date(profile.shiftStartedAt);
        if (now.getTime() - start.getTime() < 24 * 3600000) {
          hoursStr = msToHours(now.getTime() - start.getTime());
        }
      }

      setTodayStats({
        hoursWorked: hoursStr,
        deliveries: todayDeliveries.length,
        earnings: `₦${todayEarnings.toLocaleString()}`,
      });
    } catch (err) {
      console.error('Failed to load shift data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleOnline = async () => {
    const user = auth.currentUser;
    if (!user || togglingStatus) return;
    const newStatus = !isOnline;
    setTogglingStatus(true);
    try {
      if (newStatus) {
        await dbService.startShift(user.uid);
        const now = new Date();
        setShiftStartTime(now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }));
        // Save shift start time in profile
        await dbService.updateRiderProfile(user.uid, { shiftStartedAt: now } as any);
      } else {
        await dbService.updateRiderProfile(user.uid, { isOnline: false } as any);
        setShiftStartTime('');
      }
      setIsOnline(newStatus);
    } catch (err) {
      console.error('Failed to update online status:', err);
    } finally {
      setTogglingStatus(false);
    }
  };

  const weeklyPercentage = Math.min(100, Math.round((weeklyDeliveries / weeklyGoalTarget) * 100));

  return (
    <div className="absolute inset-0 bg-gray-50 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
        <button
          onClick={onBack}
          className="w-12 h-12 flex items-center justify-center hover:bg-gray-100 rounded-xl transition-colors"
          aria-label="Go back"
        >
          <ArrowLeft className="w-6 h-6 text-[#111111]" />
        </button>
        <div>
          <h1 className="text-[18px] text-[#111111]">Shift Management</h1>
          <p className="text-[12px] text-[#6B6B6B]">Track your active shift</p>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 space-y-4">
        {/* Online Status Toggle */}
        <div className={`rounded-[16px] p-4 ${isOnline ? 'bg-[#00B277]' : 'bg-gray-300'}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isOnline ? 'bg-white/20' : 'bg-black/10'}`}>
                {isOnline ? <Play className="w-6 h-6 text-white" /> : <Pause className="w-6 h-6 text-gray-600" />}
              </div>
              <div>
                <p className={`text-[16px] ${isOnline ? 'text-white' : 'text-gray-700'}`}>
                  {isOnline ? "You're Online" : "You're Offline"}
                </p>
                <p className={`text-[12px] ${isOnline ? 'text-white/80' : 'text-gray-600'}`}>
                  {isOnline ? 'Receiving delivery requests' : 'Not receiving requests'}
                </p>
              </div>
            </div>
            <button
              onClick={handleToggleOnline}
              disabled={togglingStatus}
              className={`relative w-16 h-12 rounded-full transition-colors ${
                isOnline ? 'bg-white/30' : 'bg-gray-400'
              } disabled:opacity-60`}
            >
              {togglingStatus ? (
                <Loader2 className="w-5 h-5 animate-spin text-white absolute top-3.5 left-5" />
              ) : (
                <div className={`absolute top-1 w-10 h-10 rounded-full bg-white shadow-md transition-transform ${isOnline ? 'translate-x-5' : 'translate-x-1'}`} />
              )}
            </button>
          </div>

          {isOnline && shiftStartTime && (
            <div className="flex items-center gap-2 text-white/90 text-[12px]">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
              Active since {shiftStartTime}
            </div>
          )}
        </div>

        {/* Current Shift Stats */}
        <div className="bg-white rounded-[16px] p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[16px] text-[#111111]">Today's Shift</h2>
            {loading && <Loader2 className="w-4 h-4 text-[#6B6B6B] animate-spin" />}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-[12px] p-3">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-[#E0001B]" />
                <span className="text-[12px] text-[#6B6B6B]">Hours Worked</span>
              </div>
              <p className="text-[20px] text-[#111111]">{todayStats.hoursWorked}</p>
            </div>

            <div className="bg-gray-50 rounded-[12px] p-3">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-4 h-4 text-[#00B277]" />
                <span className="text-[12px] text-[#6B6B6B]">Deliveries</span>
              </div>
              <p className="text-[20px] text-[#111111]">{todayStats.deliveries}</p>
            </div>

            <div className="bg-gray-50 rounded-[12px] col-span-2 p-3">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-[#00B277]" />
                <span className="text-[12px] text-[#6B6B6B]">Today's Earnings</span>
              </div>
              <p className="text-[20px] text-[#00B277]">{todayStats.earnings}</p>
            </div>
          </div>
        </div>

        {/* Weekly Goal */}
        <div className="bg-white rounded-[16px] p-4">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-5 h-5 text-[#E0001B]" />
            <h2 className="text-[16px] text-[#111111]">Weekly Goal</h2>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[14px] text-[#6B6B6B]">Deliveries</span>
              <span className="text-[14px] text-[#111111]">
                {weeklyDeliveries} / {weeklyGoalTarget}
              </span>
            </div>

            <div className="relative w-full h-3 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="absolute top-0 left-0 h-full bg-gradient-to-r from-[#E0001B] to-[#FF1A35] rounded-full transition-all duration-500"
                style={{ width: `${weeklyPercentage}%` }}
              />
            </div>

            <p className="text-[12px] text-[#6B6B6B]">
              {weeklyDeliveries >= weeklyGoalTarget
                ? '🎉 Weekly goal reached!'
                : `${weeklyGoalTarget - weeklyDeliveries} more deliveries to reach your goal`}
            </p>
          </div>
        </div>

        {/* Refresh */}
        <button
          onClick={loadData}
          className="w-full h-12 rounded-[12px] border-2 border-dashed border-gray-300 text-[#6B6B6B] hover:border-[#E0001B] hover:text-[#E0001B] transition-colors flex items-center justify-center gap-2"
        >
          <TrendingUp className="w-4 h-4" />
          Refresh Stats
        </button>
      </div>
    </div>
  );
}
